import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, Avatar, Breadcrumbs, Link, CircularProgress, Alert, Divider, Chip } from '@mui/material';
import { ArrowBack, Email, Phone, Store, LocationOn } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getFoodVendorById } from '../../services/Api';

const FoodVendorDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchVendor = async () => {
            try {
                const response = await getFoodVendorById(id);
                if (response.success || response.data) {
                    setVendor(response.data || response);
                } else {
                    setError(response?.message || 'Failed to fetch food vendor details');
                }
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.message || err.message || 'An error occurred while fetching details');
            } finally {
                setLoading(false);
            }
        };
        fetchVendor();
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!vendor) {
        return <Alert severity="warning">Food Vendor not found.</Alert>;
    }

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/vendors/food'); }} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        Food Vendors
                    </Link>
                    <Typography color="text.primary">Vendor Details</Typography>
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Food Vendor Details
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 3 }}>
                    <Avatar src={vendor.image} alt={vendor.shopName} sx={{ width: 100, height: 100 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{vendor.shopName}</Typography>
                        <Chip size="small" label={vendor.isActive ? "Active" : "Inactive"} color={vendor.isActive ? "success" : "default"} />
                    </Box>
                </Box>

                <Divider sx={{ mb: 4 }} />

                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Store color="action" sx={{ mr: 2 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Owner Name</Typography>
                                <Typography variant="body1">{vendor.ownerName || 'N/A'}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <LocationOn color="action" sx={{ mr: 2, mt: 0.5 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Full Address</Typography>
                                {vendor.addresses && vendor.addresses.length > 0 ? (
                                    <Typography variant="body1">
                                        {vendor.addresses[0].addressLine1}{vendor.addresses[0].addressLine2 ? `, ${vendor.addresses[0].addressLine2}` : ''}
                                        <br />
                                        {vendor.addresses[0].landmark && `${vendor.addresses[0].landmark}, `}
                                        {vendor.addresses[0].city && `${vendor.addresses[0].city}, `}{vendor.addresses[0].state && `${vendor.addresses[0].state} `}{vendor.addresses[0].pincode}
                                    </Typography>
                                ) : (
                                    <Typography variant="body1" color="text.secondary">No address details available</Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Phone color="action" sx={{ mr: 2 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Mobile number</Typography>
                                <Typography variant="body1">{vendor.mobile || 'N/A'}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Email color="action" sx={{ mr: 2 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Email Address</Typography>
                                <Typography variant="body1">{vendor.email || 'N/A'}</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 4, mt: 4 }} />

                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">Joined Date</Typography>
                        <Typography variant="body1">{vendor.createdAt ? new Date(vendor.createdAt).toLocaleDateString() : 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">Average Rating</Typography>
                        <Typography variant="body1">{vendor.rating ? `${vendor.rating} (${vendor.ratingCount} reviews)` : '-'}</Typography>
                    </Grid>
                    {/* <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">Delivery Time</Typography>
                        <Typography variant="body1">{vendor.deliveryTime || 'N/A'}</Typography>
                    </Grid> */}
                </Grid>
            </Paper>
        </Box>
    );
};

export default FoodVendorDetails;
